'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { urlFor } from '@/sanity/lib/image';
import { t } from '@/i18n/translations';
import 'maplibre-gl/dist/maplibre-gl.css';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Подложка OpenFreeMap: без аккаунта, ключа и лимитов, коммерческое использование разрешено.
// До 02.10.2026 был Mapbox — счёт за перерасход и угроза отключить аккаунт (CLAUDE.md, «Платные API»).
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

const validPts = (arr) => (arr || []).filter((p) => p?.coords?.lat && p?.coords?.lng);

// Маленький значок самолёта для маркеров-аэропортов.
const PLANE_SVG = `<svg viewBox="0 0 24 24" width="12" height="12" fill="#fff" aria-hidden="true"><path d="M21 15.5v-1.7l-7.5-4.6V3.6a1.5 1.5 0 0 0-3 0v5.6L3 13.8v1.7l7.5-2.2v4.4l-2 1.4v1.3l3.5-1 3.5 1v-1.3l-2-1.4v-4.4z"/></svg>`;

/**
 * GfInteractiveMap — переиспользуемая карта Gallery·Field: карта + боковая панель.
 *
 * Модель — СЛОИ-ФИЛЬТРЫ. У каждого слоя свой тип маркера (форма + цвет), чтобы точки
 * разных разделов отличались друг от друга. В легенде каждый слой — переключатель
 * (чип-чекбокс): можно показать все сразу или выключить отдельные. Один «listed»-слой
 * (по умолчанию города/места) дополнительно получает нумерованный список в панели;
 * клик по пункту → перелёт к точке и попап. Опциональный центр-маркер (город).
 *
 * layer: { key, kind?, label, swatch, listed?, toggleable?, kicker?,
 *          points:[{ id, title, subtitle?, coords:{lat,lng}, cover?, href?, meta? }] }
 * center: { coords:{lat,lng}, label } | null
 */
export default function GfInteractiveMap({
    layers = [],
    center = null,
    panelEyebrow = '',
    hint = '',
    ariaLabel = '',
    lang = 'ru',
    zoom = 12,
    fitMaxZoom = 15,
}) {
    const ui = t(lang, 'mapsUi');
    const mapContainer = useRef(null);
    const map = useRef(null);
    const allPopupsRef = useRef([]);
    const layerMarkersRef = useRef({});   // key → [маркеры] (по порядку точек)
    const layerElsRef = useRef({});       // key → [DOM-элементы] (подсветка активного listed-маркера)
    const centerMarkerRef = useRef(null);
    const listRef = useRef(null);
    const mapLoadedRef = useRef(false);
    const visibleRef = useRef(null);      // актуальный набор видимых слоёв (для обработчиков вне рендера)
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const [visibleKeys, setVisibleKeys] = useState(null);   // null → все слои видимы (значение по умолчанию)

    const mapReady = mapLoaded && !mapError;

    const layerData = useMemo(
        () => (layers || []).map((l) => ({ ...l, points: validPts(l.points) })).filter((l) => l.points.length > 0),
        [layers]
    );

    // listed-слой: его точки нумеруются и выводятся списком в панели.
    const listedLayer = useMemo(() => layerData.find((l) => l.listed) || layerData[0] || null, [layerData]);
    const listedKey = listedLayer?.key || null;

    const isVisible = (key) => (visibleKeys ? visibleKeys.has(key) : true);
    const isKeyVisible = (key) => (visibleRef.current ? visibleRef.current.has(key) : true);

    useEffect(() => {
        visibleRef.current = visibleKeys;
    }, [visibleKeys]);

    const toggleLayer = (key) => {
        setVisibleKeys((prev) => {
            const base = prev ? new Set(prev) : new Set(layerData.map((l) => l.key));
            if (base.has(key)) base.delete(key); else base.add(key);
            return base;
        });
    };

    const listVisible = !!listedKey && isVisible(listedKey);
    const listPoints = listVisible ? (listedLayer?.points || []) : [];
    const hasPoints = layerData.length > 0;

    const centerCoords = center?.coords?.lat && center?.coords?.lng ? center.coords : null;
    const centerLabel = center?.label || '';

    const mapCenter = useMemo(() => {
        if (centerCoords) return centerCoords;
        const all = layerData.flatMap((l) => l.points);
        if (all.length === 0) return null;
        const sLat = all.reduce((s, p) => s + p.coords.lat, 0);
        const sLng = all.reduce((s, p) => s + p.coords.lng, 0);
        return { lat: sLat / all.length, lng: sLng / all.length };
    }, [centerCoords, layerData]);

    const getImageUrl = (imageData, width = 520, height = 248) => {
        if (!imageData) return null;
        try {
            let source = Array.isArray(imageData) ? imageData[0] : imageData;
            source = source?.image || source;
            if (!source) return null;
            // Guard: a missing asset _ref makes @sanity/image-url do ref.split('-')
            // on undefined → "Cannot read properties of undefined (reading 'split')".
            const ref = source?.asset?._ref || source?._ref;
            const id = source?.asset?._id || source?._id;
            if (!ref && !id) return null;
            return urlFor(source).width(width).height(height).fit('crop').url();
        } catch (e) {
            console.warn('Failed to build image URL:', e);
            return null;
        }
    };

    // Подгон: попап целиком в кадре (после раскладки + загрузки картинки, не во время движения).
    const fitPopupInView = (popup) => {
        const run = () => {
            if (!map.current || (map.current.isMoving && map.current.isMoving())) return;
            const el = popup?.getElement?.();
            if (!el) return;
            const mr = map.current.getContainer().getBoundingClientRect();
            const pr = el.getBoundingClientRect();
            const pad = 14;
            let dx = 0, dy = 0;
            if (pr.left < mr.left + pad) dx = pr.left - mr.left - pad;
            else if (pr.right > mr.right - pad) dx = pr.right - mr.right + pad;
            if (pr.top < mr.top + pad) dy = pr.top - mr.top - pad;
            else if (pr.bottom > mr.bottom - pad) dy = pr.bottom - mr.bottom + pad;
            if (dx || dy) map.current.panBy([dx, dy], { duration: prefersReducedMotion() ? 0 : 280 });
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
        const img = popup?.getElement?.()?.querySelector('img');
        if (img && !img.complete) img.addEventListener('load', run, { once: true });
    };

    // Подгон камеры под все видимые точки (+ центр).
    const fitToVisible = () => {
        if (!map.current) return;
        const reduce = prefersReducedMotion();
        const coords = [];
        layerData.forEach((l) => { if (isKeyVisible(l.key)) l.points.forEach((p) => coords.push(p.coords)); });
        if (centerCoords) coords.push(centerCoords);
        if (coords.length === 0) return;
        if (coords.length === 1) {
            map.current.flyTo({ center: [coords[0].lng, coords[0].lat], zoom: Math.min(13, fitMaxZoom), duration: reduce ? 0 : 600, essential: true });
            return;
        }
        let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
        coords.forEach((c) => {
            minLng = Math.min(minLng, c.lng); maxLng = Math.max(maxLng, c.lng);
            minLat = Math.min(minLat, c.lat); maxLat = Math.max(maxLat, c.lat);
        });
        map.current.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: { top: 50, bottom: 50, left: 50, right: 50 }, maxZoom: fitMaxZoom, duration: reduce ? 0 : 600 });
    };

    const focusPoint = (i) => {
        const p = listedLayer?.points?.[i];
        const marker = layerMarkersRef.current[listedKey]?.[i];
        if (!map.current || !p?.coords) return;
        setActiveIndex(i);
        const pop = marker?.getPopup?.();
        if (pop && !pop.isOpen()) marker.togglePopup();
        map.current.flyTo({
            center: [p.coords.lng, p.coords.lat],
            zoom: Math.max(map.current.getZoom(), 14),
            duration: prefersReducedMotion() ? 0 : 800,
            essential: true,
        });
        if (pop) map.current.once('moveend', () => fitPopupInView(pop));
    };

    // Подсветка активного listed-маркера + автоскролл активной строки.
    useEffect(() => {
        Object.values(layerElsRef.current).forEach((els) => els.forEach((el) => el?.querySelector('.cmm-pin')?.classList.remove('is-active')));
        if (activeIndex == null || !listedKey) return;
        layerElsRef.current[listedKey]?.[activeIndex]?.querySelector('.cmm-pin')?.classList.add('is-active');
        const row = listRef.current?.querySelector('.gf-map-row.is-active');
        row?.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }, [activeIndex, listedKey]);

    // Переключение видимости слоёв: показать/скрыть маркеры, закрыть попапы скрытых, подогнать камеру.
    useEffect(() => {
        if (!mapLoaded || !map.current) return;
        layerData.forEach((layer) => {
            const show = isKeyVisible(layer.key);
            (layerMarkersRef.current[layer.key] || []).forEach((m) => {
                const el = m.getElement?.();
                if (el) el.style.display = show ? '' : 'none';
                if (!show) { const pp = m.getPopup?.(); if (pp?.isOpen?.()) pp.remove(); }
            });
        });
        if (!listedKey || !isKeyVisible(listedKey)) setActiveIndex(null);
        fitToVisible();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleKeys, mapLoaded]);

    useEffect(() => {
        if (!mapCenter || !mapContainer.current || map.current) return;

        let cancelled = false;

        const loadMap = async () => {
            try {
                const mod = await import('maplibre-gl');
                const maplibregl = mod.default ?? mod;
                if (cancelled || map.current || !mapContainer.current) return;

                map.current = new maplibregl.Map({
                    container: mapContainer.current,
                    style: BASEMAP_STYLE,
                    center: [mapCenter.lng, mapCenter.lat],
                    zoom,
                    attributionControl: false,
                    // На телефоне карта 440 px перехватывала вертикальный свайп — страница не
                    // прокручивалась (ревью 30.09.2026). Двумя пальцами — карта, одним — страница.
                    cooperativeGestures: typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches,
                });
                map.current.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
                map.current.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

                map.current.on('load', () => {
                    mapLoadedRef.current = true;

                    // liberty подписывает в две строки «латиница + местное письмо» (Будва,
                    // Тбилиси, Доха); оставляем одну — на языке страницы, иначе латиницей.
                    const labelField = ['coalesce', ['get', `name:${lang}`], ['get', 'name:latin'], ['get', 'name']];
                    map.current.getStyle().layers.forEach((l) => {
                        const tf = l.type === 'symbol' && l.layout?.['text-field'];
                        if (tf && JSON.stringify(tf).includes('name')) map.current.setLayoutProperty(l.id, 'text-field', labelField);
                    });

                    layerMarkersRef.current = {};
                    layerElsRef.current = {};
                    allPopupsRef.current = [];

                    const makePopup = (p, eyebrow, isListed, idx) => {
                        const imageUrl = getImageUrl(p.cover, 520, 248);
                        const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${p.coords.lat},${p.coords.lng}`;
                        const html = `
                            <div class="cmap-popup-inner">
                                ${imageUrl ? `<div class="cmap-popup-img"><img src="${imageUrl}" alt="${esc(p.title)}"/></div>` : ''}
                                <div class="cmap-popup-body">
                                    <span class="cmap-popup-label">${eyebrow}</span>
                                    <h3 class="cmap-popup-title">${esc(p.title)}</h3>
                                    ${p.subtitle ? `<p class="cmap-popup-subtitle">${esc(p.subtitle)}</p>` : ''}
                                    <div class="cmap-popup-actions">
                                        ${p.href ? `<a href="${esc(p.href)}" class="cmap-popup-btn">${ui.popupExplore}</a>` : ''}
                                        <a href="${directionsUrl}" target="_blank" rel="noopener noreferrer" class="cmap-popup-btn-icon" title="${ui.popupDirections}"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7"/><path d="M8 7h9v9"/></svg></a>
                                    </div>
                                </div>
                            </div>`;
                        const popup = new maplibregl.Popup({ offset: 18, closeButton: true, closeOnClick: true, maxWidth: '260px', className: 'gf-map-popup', focusAfterOpen: false }).setHTML(html);
                        allPopupsRef.current.push(popup);
                        popup.on('open', () => {
                            allPopupsRef.current.forEach((pp) => { if (pp !== popup && pp.isOpen?.()) pp.remove(); });
                            setActiveIndex(isListed ? idx : null);
                            fitPopupInView(popup);
                        });
                        popup.on('close', () => { if (isListed) setActiveIndex((prev) => (prev === idx ? null : prev)); });
                        return popup;
                    };

                    if (centerCoords) {
                        const cEl = document.createElement('div');
                        cEl.className = 'gf-map-marker-center';
                        cEl.innerHTML = `<div class="cmm-city" title="${esc(centerLabel)}"></div>`;
                        centerMarkerRef.current = new maplibregl.Marker({ element: cEl }).setLngLat([centerCoords.lng, centerCoords.lat]).addTo(map.current);
                    }

                    layerData.forEach((layer) => {
                        const isListed = layer.key === listedKey;
                        const swatch = layer.swatch || (isListed ? 'var(--pine)' : 'var(--stamp)');
                        layerMarkersRef.current[layer.key] = [];
                        layerElsRef.current[layer.key] = [];
                        layer.points.forEach((p, idx) => {
                            const el = document.createElement('div');
                            el.className = 'gf-map-marker-point';
                            // Listed-слой (на карте страны — города) рисуем поверх точечных
                            // слоёв (достопримечательности/аэропорты): маркеры
                            // стакаются по порядку в DOM, поэтому задаём z-index явно.
                            el.style.zIndex = isListed ? '2' : '1';
                            if (isListed) {
                                el.innerHTML = `<div class="cmm-pin" style="--pin-bg:${swatch}">${idx + 1}</div>`;
                            } else {
                                const shape = layer.kind === 'airport' ? 'sq' : 'rd';
                                const icon = layer.kind === 'airport' ? PLANE_SVG : '';
                                el.innerHTML = `<div class="cmm-glyph cmm-glyph--${shape}" style="--g:${swatch}" title="${esc(layer.label || '')}">${icon}</div>`;
                            }
                            layerElsRef.current[layer.key][idx] = el;
                            const eyebrow = isListed
                                ? `${esc(String(idx + 1).padStart(2, '0'))}${p.meta ? ` · ${esc(p.meta)}` : ''}`
                                : `${esc(layer.kicker || layer.label || '')}${p.meta ? ` · ${esc(p.meta)}` : ''}`;
                            const popup = makePopup(p, eyebrow, isListed, idx);
                            const marker = new maplibregl.Marker({ element: el }).setLngLat([p.coords.lng, p.coords.lat]).setPopup(popup).addTo(map.current);
                            layerMarkersRef.current[layer.key][idx] = marker;
                        });
                    });

                    // начальная видимость по текущему набору + первичный подгон камеры
                    layerData.forEach((layer) => {
                        const show = isKeyVisible(layer.key);
                        layerMarkersRef.current[layer.key].forEach((m) => { const el = m.getElement(); if (el) el.style.display = show ? '' : 'none'; });
                    });
                    setMapLoaded(true);
                    fitToVisible();
                });

                map.current.on('error', (e) => {
                    console.error('Map error:', e);
                    if (!cancelled && !mapLoadedRef.current) {
                        setMapError(ui.errorLoad);
                        setActiveIndex(null);
                    }
                });
            } catch (err) {
                console.error('Failed to load map:', err);
                if (!cancelled) setMapError(ui.errorFailed);
            }
        };

        loadMap();

        return () => {
            cancelled = true;
            Object.values(layerMarkersRef.current).flat().forEach((m) => m?.remove());
            layerMarkersRef.current = {};
            layerElsRef.current = {};
            allPopupsRef.current = [];
            centerMarkerRef.current?.remove();
            centerMarkerRef.current = null;
            mapLoadedRef.current = false;
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapCenter, centerCoords, centerLabel, layerData, lang, zoom, fitMaxZoom]);

    if (!mapCenter) return null;

    return (
        <div className="gf-map">
            <style jsx global>{`
                /* ── markers ── */
                .cmm-pin {
                    width: 28px; height: 28px; border-radius: 50%;
                    background: var(--pin-bg, var(--pine, #1e466e)); color: #fff;
                    border: 2px solid #fff; box-shadow: 0 3px 10px rgba(23,33,28,.35);
                    display: flex; align-items: center; justify-content: center;
                    font: 700 12px/1 var(--sans, system-ui); letter-spacing: -0.02em;
                    cursor: pointer; transition: transform .18s ease, box-shadow .18s ease, background .18s ease;
                }
                .cmm-pin:hover { transform: scale(1.14); box-shadow: 0 6px 16px rgba(23,33,28,.45); }
                .cmm-pin.is-active { background: var(--stamp, #b23a2e); transform: scale(1.18); box-shadow: 0 6px 18px rgba(178,58,46,.5); }
                .cmm-city { width: 16px; height: 16px; border-radius: 50%; background: var(--stamp, #b23a2e); border: 3px solid #fff; box-shadow: 0 2px 8px rgba(23,33,28,.4); }

                /* распознаваемые маркеры неподписанных слоёв (места / аэропорты) */
                .cmm-glyph {
                    display: flex; align-items: center; justify-content: center;
                    border: 2px solid #fff; box-shadow: 0 3px 9px rgba(23,33,28,.34);
                    cursor: pointer; transition: transform .18s ease, box-shadow .18s ease;
                }
                .cmm-glyph:hover { transform: scale(1.18); box-shadow: 0 6px 15px rgba(23,33,28,.44); }
                .cmm-glyph--rd { width: 19px; height: 19px; border-radius: 50%; background: var(--g, var(--stamp, #b23a2e)); position: relative; }
                .cmm-glyph--rd::after { content: ''; position: absolute; inset: 5px; background: #fff; border-radius: 50%; }
                .cmm-glyph--sq { width: 21px; height: 21px; border-radius: 4px; background: var(--g, var(--ink, #17211c)); }

                /* ── GF popup ── */
                .gf-map-popup .maplibregl-popup-content { padding: 0; border-radius: 0; background: var(--surface, #fff); border: 1px solid var(--line, #ded9cd); box-shadow: 0 24px 50px -24px rgba(23,33,28,0.45); overflow: hidden; font-family: var(--sans, system-ui); }
                .gf-map-popup .maplibregl-popup-tip { display: none; }
                .gf-map-popup .maplibregl-popup-close-button {
                    width: 28px; height: 28px; right: 8px; top: 8px; z-index: 2; padding: 0;
                    font-size: 0; color: transparent; border-radius: 0;
                    background-color: var(--surface, #fff); border: 1px solid var(--line, #ded9cd);
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 11 11' fill='none' stroke='%237c766a' stroke-width='1.3' stroke-linecap='round'%3E%3Cpath d='M1 1l9 9M10 1l-9 9'/%3E%3C/svg%3E");
                    background-repeat: no-repeat; background-position: center; background-size: 11px 11px;
                    transition: background-color .15s ease, border-color .15s ease;
                }
                .gf-map-popup .maplibregl-popup-close-button:hover {
                    background-color: var(--line-2, #f1efe9); border-color: var(--ink, #17211c);
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 11 11' fill='none' stroke='%2317211c' stroke-width='1.5' stroke-linecap='round'%3E%3Cpath d='M1 1l9 9M10 1l-9 9'/%3E%3C/svg%3E");
                }
                .cmap-popup-inner { width: 256px; }
                .cmap-popup-img { position: relative; height: 124px; overflow: hidden; border-bottom: 1px solid var(--line, #ded9cd); }
                .cmap-popup-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .cmap-popup-body { padding: 13px 15px 15px; }
                .cmap-popup-label { display: block; font-family: var(--mono, monospace); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.16em; color: var(--pine, #1e466e); margin-bottom: 6px; }
                .cmap-popup-title { font-family: var(--sans, system-ui); font-size: 15px; font-weight: 700; color: var(--ink, #17211c); line-height: 1.25; margin: 0; letter-spacing: -0.015em; }
                .cmap-popup-subtitle { font-family: var(--sans, system-ui); font-size: 12.5px; color: var(--muted, #7c766a); line-height: 1.5; margin: 6px 0 0; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
                .cmap-popup-actions { display: flex; gap: 7px; margin-top: 14px; }
                .cmap-popup-btn { flex: 1; display: flex; align-items: center; justify-content: center; padding: 9px 12px; background: var(--pine, #1e466e); color: #fff !important; border: 1px solid var(--pine, #1e466e); border-radius: 0; font-family: var(--mono, monospace); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; text-decoration: none !important; transition: opacity 0.15s ease; }
                .cmap-popup-btn:hover { opacity: 0.88; }
                .cmap-popup-btn-icon { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: var(--surface, #fff); color: var(--pine, #1e466e) !important; border: 1px solid var(--line, #ded9cd); border-radius: 0; text-decoration: none !important; flex-shrink: 0; transition: background 0.15s ease, color 0.15s ease; }
                .cmap-popup-btn-icon:hover { background: var(--pine, #1e466e); color: #fff !important; }

                /* ── controls ── */
                .gf-map-frame .maplibregl-ctrl-group { border-radius: 0; box-shadow: none; border: 1px solid var(--line, #ded9cd); background: var(--surface, #fff); overflow: hidden; }
                .gf-map-frame .maplibregl-ctrl-group button { width: 32px; height: 32px; border-radius: 0 !important; background-color: var(--surface, #fff); transition: background-color .15s ease; }
                .gf-map-frame .maplibregl-ctrl-group button + button { border-top: 1px solid var(--line, #ded9cd); }
                .gf-map-frame .maplibregl-ctrl-group button:hover { background-color: var(--line-2, #f1efe9); }
                .gf-map-frame .maplibregl-ctrl-group button:focus { box-shadow: none; }
                .gf-map-frame .maplibregl-ctrl-group button:focus-visible { outline: 2px solid var(--pine, #1e466e); outline-offset: -2px; }
                .gf-map-frame .maplibregl-ctrl-attrib { border-radius: 0; background: rgba(255,255,255,0.86); }
                .gf-map-frame .maplibregl-ctrl-attrib.maplibregl-compact,
                .gf-map-frame .maplibregl-ctrl-attrib-button { border-radius: 0; }
                .gf-map-frame .maplibregl-ctrl-attrib-inner { font-family: var(--mono, monospace); font-size: 10px; color: var(--muted, #7c766a); }

                /* ── layer toggles / keys ── */
                .gf-map-center-note { display: flex; align-items: center; gap: 9px; margin-bottom: 12px; font-family: var(--mono, monospace); font-size: 10px; text-transform: uppercase; letter-spacing: .1em; color: var(--muted, #7c766a); }
                .gf-map-center-note .dot { width: 10px; height: 10px; border-radius: 50%; background: var(--stamp, #b23a2e); flex-shrink: 0; }
                .gf-map-tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
                .gf-map-tab { display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px; background: var(--surface, #fff); border: 1px solid var(--line, #ded9cd); color: var(--ink, #17211c); font-family: var(--mono, monospace); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .08em; cursor: pointer; transition: background .15s ease, color .15s ease, border-color .15s ease, opacity .15s ease; }
                .gf-map-tab:hover:not(:disabled) { border-color: var(--ink, #17211c); }
                .gf-map-tab:disabled { opacity: .5; cursor: default; }
                .gf-map-tab:focus-visible { outline: 2px solid var(--pine, #1e466e); outline-offset: 2px; }
                .gf-map-tab .dot { width: 9px; height: 9px; flex-shrink: 0; }
                .gf-map-tab .cnt { opacity: .6; }
                .gf-map-tog .tg { position: relative; width: 13px; height: 13px; flex-shrink: 0; border: 1.5px solid var(--line, #ded9cd); border-radius: 3px; background: var(--surface, #fff); transition: background .15s ease, border-color .15s ease; }
                .gf-map-tog.is-on .tg { background: var(--ink, #17211c); border-color: var(--ink, #17211c); }
                .gf-map-tog.is-on .tg::after { content: ''; position: absolute; left: 3.5px; top: 0.5px; width: 3.5px; height: 7px; border: solid #fff; border-width: 0 1.6px 1.6px 0; transform: rotate(45deg); }
                .gf-map-tog.is-off { opacity: .5; }
                .gf-map-tog.is-off .dot { opacity: .55; }
                .gf-map-keys .gf-map-key-cnt { margin-left: auto; opacity: .6; font-variant-numeric: tabular-nums; }
            `}</style>

            <div className="gf-map-frame">
                <div ref={mapContainer} role="region" aria-label={ariaLabel || ui.ariaMap} />
                {!mapLoaded && !mapError && (
                    <div className="gf-map-state">
                        <span className="gf-map-spin" aria-hidden />
                        <span className="gf-map-state-t">{ui.loading}</span>
                    </div>
                )}
                {mapError && (
                    <div className="gf-map-state">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden style={{ color: 'var(--muted)' }}>
                            <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                        <span className="gf-map-state-t">{mapError}</span>
                    </div>
                )}
            </div>

            <aside className={`gf-map-legend${!hasPoints ? ' gf-map-legend--compact' : ''}`}>
                <span className="gf-map-eyebrow">{panelEyebrow}</span>

                {centerCoords && (
                    <span className="gf-map-center-note"><span className="dot" /> {centerLabel || ui.centerNote}</span>
                )}

                {layerData.length > 1 ? (
                    <div className="gf-map-tabs" role="group" aria-label={ui.ariaLayers}>
                        {layerData.map((l) => {
                            const on = isVisible(l.key);
                            return (
                                <button
                                    key={l.key}
                                    type="button"
                                    aria-pressed={on}
                                    className={`gf-map-tab gf-map-tog${on ? ' is-on' : ' is-off'}`}
                                    onClick={() => toggleLayer(l.key)}
                                    disabled={!mapReady}
                                >
                                    <span className="tg" aria-hidden />
                                    <span className="dot" style={{ background: l.swatch || 'var(--pine)', borderRadius: l.kind === 'airport' ? '2px' : '50%' }} />
                                    {l.label}<span className="cnt">{l.points.length}</span>
                                </button>
                            );
                        })}
                    </div>
                ) : (layerData.length === 1 && (
                    <div className="gf-map-keys">
                        <span className="lg"><span className="dot" style={{ background: layerData[0].swatch || 'var(--pine)' }} /> {layerData[0].label}<span className="gf-map-key-cnt">{layerData[0].points.length}</span></span>
                    </div>
                ))}

                {!mapError && listPoints.length > 0 && (
                    <ol className="gf-map-list" ref={listRef}>
                        {listPoints.map((p, i) => (
                            <li key={p.id || i}>
                                <button
                                    type="button"
                                    className={`gf-map-row${activeIndex === i ? ' is-active' : ''}`}
                                    onClick={() => focusPoint(i)}
                                    disabled={!mapReady}
                                    aria-pressed={activeIndex === i}
                                >
                                    <span className="ix">{String(i + 1).padStart(2, '0')}</span>
                                    <span className="gf-map-row-t">{p.title}</span>
                                    {p.meta && <span className="gf-map-row-meta">{p.meta}</span>}
                                    <span className="gf-map-row-go" aria-hidden>→</span>
                                </button>
                            </li>
                        ))}
                    </ol>
                )}

                {mapError ? (
                    <div className="gf-map-fallback">
                        {(listedLayer?.points?.length || 0) > 0 && (
                            <ul className="gf-map-fallback-list">
                                {listedLayer.points.map((p, i) => (
                                    <li key={p.id || i}>
                                        <span className="ix">{String(i + 1).padStart(2, '0')}</span>
                                        <span>{p.title}</span>
                                        {p.meta && <span className="km">{p.meta}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <a className="gf-map-fallback-link" href={`https://www.google.com/maps/search/?api=1&query=${mapCenter.lat},${mapCenter.lng}`} target="_blank" rel="noopener noreferrer">
                            {ui.openInGoogle}
                        </a>
                    </div>
                ) : listPoints.length > 0 ? (
                    <span className="gf-map-hint">{hint}</span>
                ) : hasPoints ? (
                    <span className="gf-map-hint">{ui.enableLayerHint}</span>
                ) : null}
            </aside>
        </div>
    );
}
